import _ from "lodash";
import moment from "moment";
import {
  AdditionalChargeNames,
  ChargeLabels,
} from "../pages/customer-management/CustomerRateTabs/CustomerAdditionalCharges/CustomerAdditionalCharges.Types";
import { useEffect } from "react";
/**
 * Markup Types.
 */
export enum MarkupValueType {
  FIXED = "fixed",
  PERCENT = "percent",
}

/**
 * Enumeration of user roles in the system.
 */
export enum Roles {
  SUPER_ADMIN = "superAdmin",
  MANAGER = "manager",
  ACCOUNTING = "accounting",
  ADMIN = "admin",
  ADMINISTRATOR = "administrator",
  AGENT = "agent",
  AGENT_GROUP = "agentGroup",
  CONTRACTOR = "contractor",
  CUSTOMER = "customer",
  CUSTOMER_GROUP = "customerGroup",
  DISPATCHER = "dispatcher",
  EMPLOYEE = "employee",
  OPERATOR = "operator",
  SALES = "sales",
  SALES_REP = "salesrep",
}

/**
 * Dropdown options for selecting roles in the system.
 */
export const RolesDropdown = [
  { label: "Super Admin", value: "superAdmin" },
  { label: "Admin", value: "admin" },
  { label: "Manager", value: "manager" },
  { label: "Employee", value: "employee" },
];

/**
 * Dropdown options for selecting user roles in the system.
 */
export const UserRolesDropdown = [
  { label: "Admin", value: "admin" },
  { label: "Manager", value: "manager" },
  { label: "Employee", value: "employee" },
];

/**
 * Determines whether a button should be disabled based on the presence of errors or unchanged values.
 *
 * @param {any} error - The error object containing any validation errors.
 * @param {any} value - The current form values.
 * @param {any} initialValues - The initial form values.
 * @returns {boolean} - Returns `true` if the button should be disabled, otherwise `false`.
 */
export const isButtonDisabled = (
  error: any,
  value: any,
  initialValues: any
) => {
  let status = false;

  if (Object.keys(error)?.length !== 0) {
    status = true;
  } else if (
    Object.keys(error)?.length === 0 &&
    _.isEqual(value, initialValues)
  ) {
    status = true;
  }
  return status;
};

/**
 * Converts an email address to a partially masked format.
 *
 * @param {string} str - The email address to be masked.
 * @returns {string} - The masked email address.
 */
export const convertEmailToStart = (str: string) => {
  let firstTwo = str.substring(0, 2);
  let domain = str.substring(str.indexOf("@"));
  const sanitizedEmail = firstTwo + "*****" + domain;
  return sanitizedEmail;
};

export const globalMaxFileSize = 5 * 1024 * 1024;

/**
 * Capitalizes the first letter of a string.
 *
 * @param {string} str - The string to be capitalized.
 * @returns {string | undefined} - The capitalized string, or `undefined` if the input is `undefined`.
 */
export const firstLetterCapital = (str: string): string | undefined => {
  if (str) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
};

/**
 * Capitalizes the first letter of each word in a string.
 *
 * @param {string} str - The string to be formatted.
 * @returns {string} - The formatted string with each word capitalized.
 */
export const capitalizeEachWord = (str: string): string => {
  const Name = str?.split(" ");
  const capitalizedName = Name?.map((word) => {
    return word?.charAt(0).toUpperCase() + word.slice(1);
  });
  const finalName = capitalizedName?.join(" ");
  return finalName;
};

/**
 * Converts a file to a Base64-encoded string.
 *
 * @param {any} file - The file to be converted.
 * @returns {Promise<string | ArrayBuffer | null>} - A promise that resolves to the Base64-encoded string or rejects with an error.
 */
export const convertBase64 = (
  file: any
): Promise<string | ArrayBuffer | null> => {
  return new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onload = () => {
      resolve(fileReader.result);
    };
    fileReader.onerror = (error) => {
      reject(error);
    };
  });
};

/**
 * Formats a phone number based on the country code.
 *
 * @param {string} phoneNumber - The phone number to be formatted.
 * @param {string} countryCode - The country code (e.g., "+1", "+91").
 * @returns {string} - The formatted phone number.
 */
export const formatPhoneNumber = (
  phoneNumber: string,
  countryCode: string
): string => {
  if (countryCode === "+1") {
    const cleaned = ("" + phoneNumber).replace(/\D/g, "");
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return "(" + match[1] + ") " + match[2] + "-" + match[3];
    } else {
      return ("" + phoneNumber).replace(/\D/g, "");
    }
  } else if (countryCode === "+91") {
    return phoneNumber;
  } else {
    return phoneNumber;
  }
};

/**
 * Formats a phone number as it is being inputted based on the country calling code.
 *
 * @param {string} phoneNumber - The phone number being inputted.
 * @param {string} countryCallingCode - The country calling code (e.g., "+1").
 * @returns {string} - The formatted phone number.
 */
export const onInputPhNumFormat = (
  phoneNumber: string,
  countryCallingCode: string
): string => {
  const newphoneNumber = phoneNumber.replace(/\D/g, "");
  if (countryCallingCode === "+1") {
    const formatted =
      newphoneNumber.length === 0
        ? newphoneNumber
        : newphoneNumber.length < 4
          ? "(" + newphoneNumber
          : newphoneNumber.length < 7
            ? "(" +
            newphoneNumber.substring(0, 3) +
            ") " +
            newphoneNumber.substring(3, 6)
            : "(" +
            newphoneNumber.substring(0, 3) +
            ") " +
            newphoneNumber.substring(3, 6) +
            " - " +
            newphoneNumber.substring(6, 10);
    return formatted;
  } else {
    return newphoneNumber;
  }
};

/**
 * Formats a date string into a "MM/DD/YYYY" format.
 *
 * @param {string} dateString - The date string to be formatted.
 * @returns {string} - The formatted date string.
 */
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${month}/${day}/${year}`;
};

export const formatDateOnly = (dateString: string | undefined | null): string => {
  if (!dateString) return "--";
  const [y, m, d] = dateString.split("T")[0].split("-");
  return `${m}/${d}/${y}`;
};

/**
 * Formats an ISO date string into "MM/DD/YYYY hh:mm AM/PM" in local time.
 * @param dateString - ISO date string (UTC or local).
 * @returns Formatted local date-time, or "--" when empty.
 */
export const formatDateTime = (
  dateString: string | undefined | null
): string => (dateString ? moment(dateString).format("MM/DD/YYYY hh:mm A") : "--");

export function formatDateToTime(isoString: string) {
  // Create a Date object from the ISO string
  const date = new Date(isoString);

  // Get hours and minutes
  let hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();

  // Determine AM or PM suffix
  const ampm = hours >= 12 ? "PM" : "AM";

  // Convert hours from 24-hour to 12-hour format
  hours = hours % 12;
  hours = hours ? hours : 12; // the hour '0' should be '12'

  // Format minutes and seconds to ensure two digits
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(seconds).padStart(2, "0");

  // Construct the final formatted string
  return `${String(hours).padStart(
    2,
    "0"
  )}:${formattedMinutes}:${formattedSeconds} ${ampm}`;
}

/**
 * Converts a time string from UTC to the local time zone and formats it as a string with AM/PM.
 *
 * @param {string | undefined} timeString - The time string to be converted.
 * @returns {string} - The formatted local time string, or `"--"` if the input is `undefined`.
 */
export const formatOrderTimeToLocal = (
  timeString: string | undefined
): string => {
  if (!timeString) return "--";

  // Combine the time string with today's date
  const today = new Date();
  const [time, modifier] = timeString.split(" "); // Split time and AM/PM
  let [hours, minutes, seconds] = time.split(":").map(Number);

  // Adjust hours based on AM/PM
  if (modifier === "PM" && hours < 12) hours += 12;
  if (modifier === "AM" && hours === 12) hours = 0;

  // Create a Date object using the current date and provided time in UTC
  const utcDate = new Date(
    Date.UTC(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
      hours,
      minutes,
      seconds
    )
  );

  // Convert UTC time to local time
  const localDate = new Date(
    utcDate.toLocaleString("en-US", {
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    })
  );

  // Return the local time as a string with AM/PM
  return localDate.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
};

export const findArrayElementByTitle = (array: any, title: string) => {
  var ids: any = [];
  array.forEach(function (entity: any) {
    ids.push(entity[title]);
  });
  return ids;
};

export function kgToLbs(input: string | number): number {
  // Convert the input to a number if it's a string
  const kg = typeof input === "string" ? parseFloat(input) : input;

  // Ensure the value is a valid number
  if (isNaN(kg)) {
    throw new Error("Invalid input, could not convert to number");
  }
  // Conversion: 1 kg = 2.20462 lbs
  return Math.ceil(kg / 2.205);
}

export function cmToInch(input: string | number): number {
  // Convert the input to a number if it's a string
  if (input === "") {
    return 0;
  }
  const cm = typeof input === "string" ? parseFloat(input) : input;

  // Ensure the value is a valid number
  if (isNaN(cm)) {
    throw new Error("Invalid input, could not convert to number");
  }

  // Conversion: 1 cm = 0.393701 inches
  return Math.ceil(cm / 2.54);
}

export const formatDateToInput = (dateString: string | undefined): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toISOString().split("T")[0]; // This returns the date in "yyyy-MM-dd" format
};

export const formatTime = (createdAt: string) => {
  const date = new Date(createdAt);
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  return date.toLocaleTimeString("en-US", options);
};

// Helper function to convert Base64 to Blob
export const dataURItoBlob = (dataURI: string) => {
  const byteString = atob(dataURI.split(",")[1]);
  const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  return new Blob([ab], { type: mimeString });
};
export const getTodayDate = (): string => {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};
export const addDaysToDate = (date: Date, days: number): string => {
  let newDate = moment(date).add(days, "d").format("YYYY-MM-DD");
  return newDate;

  //const result = new Date(date);
  // result.setDate(result.getDate() + days);
  // const yyyy = result.getFullYear();
  // const mm = String(result.getMonth() + 1).padStart(2, "0");
  // const dd = String(result.getDate()).padStart(2, "0");
  // return `${yyyy}-${mm}-${dd}`;
};
/**
 * Converts a number (1-10) to its word form.
 * @param num - The number to convert.
 * @returns The word form of the number.
 */
export const numberToWord = (num: number): string => {
  const words = [
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
  ];
  return words[num - 1] || num.toString();
};

// Utility function to generate label or placeholder text
export const getChargeLabels = (chargeName: string | undefined) => {
  const matchingChargeName = Object.values(AdditionalChargeNames).find(
    (name) => name === chargeName
  );

  if (matchingChargeName) {
    return ChargeLabels[matchingChargeName];
  }

  // Return default values if no match is found
  return { first: "Base Value", second: "Unit Value", third: "Unit Price" };
};

/**
 * Extracts the file name from a given URL.
 *
 * @param {string} url - The URL string.
 * @returns {string} The file name from the URL, or an empty string if the URL is empty.
 *
 * @example
 * getFileNameFromUrl("http://example.com/path/to/file.txt"); // "file.txt"
 */
export const getFileNameFromUrl = (url: string) => {
  if (!url) {
    return "";
  }
  const lastBackslashIndex = url.lastIndexOf("/");
  const substringAfterLastBackslash =
    lastBackslashIndex !== -1 ? url.substring(lastBackslashIndex + 1) : url;
  return substringAfterLastBackslash;
};

export const getFileNameFromPOD = (url: string) => {
  if (!url || typeof url !== "string") {
    return "";
  }
  const lastBackslashIndex = url?.lastIndexOf("/");
  const substringAfterLastBackslash =
    lastBackslashIndex !== -1 ? url.substring(lastBackslashIndex + 1) : url;
  return (
    substringAfterLastBackslash.split("?")?.[0] || substringAfterLastBackslash
  );
};

export const getFileNameFromAWS = (url: string) => {
  if (!url) {
    return "";
  }
  return url.split("?")?.[0] || url;
};

export const normalizeDate = (date: any, time?: boolean) => {
  if (!date) return "";
  const m = moment(date);
  if (!m.isValid()) return "";
  if (time) {
    return m.format("MM-DD-YYYY hh:mm:ss A");
  }
  return m.format("YYYY-MM-DD");
};

export const convertTo24HourFormat = (time: string): string => {
  return moment(time, "h:mm A").format("HH:mm");
};

// Utility function to convert time strings to a standard 12-hour format
export const convertTo12HourFormat = (time: string): string => {
  let hours = 0;
  let minutes = 0;

  if (time.includes(":")) {
    const [h, m] = time.split(":").map(Number);
    hours = h;
    minutes = m;
  } else {
    // Handle military time format
    hours = Math.floor(Number(time) / 100);
    minutes = Number(time) % 100;
  }
  if (hours >= 24 || minutes >= 60 || isNaN(hours) || isNaN(minutes)) {
    throw new Error("Invalid time");
  }

  const period = hours >= 12 ? "PM" : "AM";
  hours = hours % 12 || 12; // Convert to 12-hour format

  return `${hours}:${minutes.toString().padStart(2, "0")} ${period}`;
};

/**
 * Times are persisted as 24-hour `HH:mm` but CustomTimePicker works in 12-hour
 * `hh:mm A`. `convertTo12HourFormat` throws on unparseable input, so anything missing
 * or malformed becomes blank instead of breaking the form.
 */
export const storedTimeToPicker = (stored?: string | null): string => {
  if (!stored) return "";
  try {
    return convertTo12HourFormat(stored);
  } catch {
    return "";
  }
};

/**
 * Inverse of `storedTimeToPicker`. `convertTo24HourFormat` hands back the literal
 * string "Invalid date" for empty/bad input, which is truthy and must never be sent.
 */
export const pickerTimeToStored = (picked?: string | null): string => {
  if (!picked) return "";
  const converted = convertTo24HourFormat(picked);
  return converted === "Invalid date" ? "" : converted;
};

/**
 * Display-only: saved dock times come back as 24-hour `HH:mm` or 12-hour `hh:mm A`
 * depending on which screen saved them. Shows both as `hh:mm A`; anything unparseable
 * is returned untouched so a value is never hidden. The literal "Invalid date" that
 * older saves stored for a blank time is treated as blank.
 */
export const formatTimeForDisplay = (value?: string | null): string => {
  if (!value || value === "Invalid date") return "";
  const parsed = moment(value, ["h:mm A", "HH:mm:ss", "HH:mm"], true);
  return parsed.isValid() ? parsed.format("hh:mm A") : value;
};

export const getDatesBetween = (
  startDate: Date | string,
  endDate: Date | string
): string[] => {
  let dates: string[] = [];
  let currentDate: Date = new Date(startDate);

  // Ensure that the end date is not before the start date
  while (currentDate <= endDate) {
    dates.push(moment(new Date(currentDate)).format("DD-MM-YYYY")); // Add current date to the array
    currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
  }

  return dates;
};

export const getWeeksBetweenDates = (
  startDate: Date,
  endDate: Date
): string[] => {
  const weeks: string[] = [];

  // Get the first day of the year for the startDate
  const startOfYear = new Date(startDate.getFullYear(), 0, 1);
  startOfYear.setHours(0, 0, 0, 0);

  // Ensure that the startDate is after or on the start of the year
  let currentStartDate = new Date(startDate);
  currentStartDate.setHours(0, 0, 0, 0);

  // Loop from the start date to the end date
  while (currentStartDate <= endDate) {
    // Calculate the number of days between the start of the year and the current date
    const diffInMs = currentStartDate.getTime() - startOfYear.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    // Calculate the week number (1-based)
    const weekNumber = Math.floor(diffInDays / 7) + 1; // +1 for 1-based indexing

    // Format the week number as two digits (e.g., "01", "02", ..., "10", etc.)
    const formattedWeekNumber = "W" + weekNumber.toString().padStart(2, "0");
    weeks.push(formattedWeekNumber);

    // Move to the next week (7 days)
    currentStartDate.setDate(currentStartDate.getDate() + 7);
  }

  return weeks;
};

export const getMonthsBetweenDates = (
  currentDate: Date,
  endDate: Date
): string[] => {
  const months: string[] = [];

  while (
    currentDate <= endDate ||
    currentDate.getMonth() === endDate.getMonth()
  ) {
    // Format month and year as "Month YYYY"
    const year = currentDate.getFullYear();

    const month = currentDate.toLocaleString("default", { month: "short" }); // Gets the full month name

    const monthName = `${year}-${month}`;
    months.push(monthName);

    // Increment the month by 1
    currentDate.setMonth(currentDate.getMonth() + 1);
  }

  return months;
};

export const convertMonthFormat = (input: string): string => {
  // Mapping of month abbreviations to numeric month values
  const monthMap: { [key: string]: string } = {
    Jan: "01",
    Feb: "02",
    Mar: "03",
    Apr: "04",
    May: "05",
    Jun: "06",
    Jul: "07",
    Aug: "08",
    Sep: "09",
    Oct: "10",
    Nov: "11",
    Dec: "12",
  };

  // Split the input into year and month parts
  const [year, month] = input.split("-");

  // Get the numeric month from the map
  const numericMonth = monthMap[month];

  // Return the formatted string as "YYYY-MM"
  return `${year}-${numericMonth}`;
};

export const getYearsBetweenDates = (
  startDate: Date,
  endDate: Date
): number[] => {
  const years: number[] = [];

  const startYear = startDate.getFullYear();
  const endYear = endDate.getFullYear();

  // Loop through the years from startYear to endYear
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }

  return years;
};

export const getLineGraphDays = (givenDate: string) => {
  const formattedDate = moment(givenDate.split("-").reverse().join("-")).format(
    "MMM DD"
  );
  return formattedDate;
};

export const getLineGraphMonths = (dateString: string) => {
  // Split the string into year and month
  const [year, month] = dateString.split("-");

  // Format it as "Dec-2024"
  const formattedDate = `${month}-${year}`;

  return formattedDate;
};

export const getISODate = (dateStr: string, timeStr: string): string => {
  const combinedDateTime = moment(
    `${dateStr} ${timeStr}`,
    "YYYY-MM-DD hh:mm:ss A"
  );
  return combinedDateTime.isValid()
    ? combinedDateTime.toISOString()
    : "Invalid date or time";
};

// Combine a date (YYYY-MM-DD or ISO) with a 24h time (HH:mm) into a
// wall-clock ISO string (YYYY-MM-DDTHH:mm:ss) with NO timezone conversion.
// Returns "" when either input is missing/invalid.
export const getDockTimeInUTC = (
  dateStr?: string,
  time24?: string
): string => {
  const isBad = (v?: string | null) =>
    !v ||
    typeof v !== "string" ||
    v.trim() === "" ||
    v.toLowerCase().includes("invalid");
  if (isBad(dateStr) || isBad(time24)) return "";
  const dateOnly = String(dateStr).includes("T")
    ? String(dateStr).split("T")[0]
    : String(dateStr);
  const m = moment(`${dateOnly} ${time24}`, "YYYY-MM-DD HH:mm");
  return m.isValid() ? m.format("YYYY-MM-DDTHH:mm:ss") : "";
};

export const extractTime = (value: string): string => {
  if (!value) return "";
  const utcMoment = moment.utc(value, moment.ISO_8601, true);
  if (utcMoment.isValid()) {
    return utcMoment.local().format("hh:mm A");
  }
  return value;
};

export const getFileType = (fileName: string) => {
  const newArr = fileName.split(".");
  if (newArr.length) {
    let reqFileName = newArr[newArr.length - 1];
    if (reqFileName.includes("?")) {
      reqFileName = reqFileName.split("?")[0];
    }
    return reqFileName.toLocaleLowerCase();
  }
  return "";
};

export const validTimeDiff = (startTime: any, endTime: any) => {
  const format = "hh:mm A"; // Define the input format
  const start = moment(startTime, format);
  const end = moment(endTime, format);

  return end.isSameOrAfter(start);
};

export const expiredData = (date: string) => {
  // Create moment objects
  const givenDate = moment(date);
  const currentDate = moment();

  // Compare the dates
  if (givenDate.isBefore(currentDate, "day")) {
    return false;
  } else {
    return true;
  }
};

// Calendar-day comparison (ignores time-of-day) so "today" is never flagged as past.
export const isPastPickupDate = (date: string): boolean => {
  if (!date) return false;
  return moment(date).isBefore(moment(), "day");
};

export const minifyHTML = (html: string) => {
  return html
    .replace(/\n/g, "") // Remove newlines
    .replace(/\s{2,}/g, " ") // Replace multiple spaces with a single space
    .replace(/<!--.*?-->/g, "") // Remove HTML comments
    .replace(/>\s</g, "><") // Remove space between tags
    .trim(); // Trim leading/trailing spaces
};

export const maskEmail = (email: string) => {
  if (!email || !email.includes("@")) return "";

  const [username, domain] = email.split("@");
  const visibleChar = username[0];
  const maskedUsername = visibleChar + "*".repeat(username.length - 1);

  return `${maskedUsername}@${domain}`;
};

export const trimDecimals = (value: any) => {
  const num = parseFloat(value);
  if (isNaN(num)) return "0.00";
  const [intPart, decPart = ""] = num.toString().split(".");
  return `${intPart}.${decPart.slice(0, 2).padEnd(2, "0")}`;
};

/** One row of the Price Details Charge / Price table. */
export interface AllianceChargeLine {
  name: string;
  amount: number;
}

/**
 * Alliance nests its accessorials under a key of the same name, and has been seen
 * both singular and plural, so unwrap rather than trust one shape. The depth guard
 * only exists so a self-referencing payload cannot spin.
 */
const toAccessorialList = (raw: any, depth = 0): any[] => {
  if (!raw || depth > 4) return [];
  if (Array.isArray(raw))
    return raw.flatMap((entry) => toAccessorialList(entry, depth + 1));
  if (typeof raw !== "object") return [];
  if (raw.AccessorialOutput !== undefined)
    return toAccessorialList(raw.AccessorialOutput, depth + 1);
  return raw.AccessorialDesc !== undefined || raw.AccessorialCharge !== undefined
    ? [raw]
    : [];
};

/**
 * Alliance prices a shipment as a flat freight charge plus a bag of accessorials,
 * instead of the chargeItemList / customerPrice breakdown the other LTL providers
 * return — which is why the Price Details table renders empty for it. Flatten that
 * into the rows the modal already knows how to draw.
 *
 * Display only: the markup the backend folds into the quote total is deliberately
 * not represented here, so these lines will not add up to the footer total.
 */
export const buildAllianceChargeLines = (offer: any): AllianceChargeLine[] => {
  const lines: AllianceChargeLine[] = [];

  // Guard the value itself, not just the coercion: Number(null) and Number("")
  // are both 0, which would print a base price the carrier never quoted.
  const rawBase = offer?.StandardFreightCharge;
  const base = Number(rawBase);
  if (rawBase !== null && rawBase !== undefined && rawBase !== "" && Number.isFinite(base)) {
    lines.push({ name: "Base Price", amount: base });
  }

  toAccessorialList(offer?.AccessorialOutput).forEach((item) => {
    const amount = Number(item?.AccessorialCharge);
    lines.push({
      name:
        capitalizeEachWord(String(item?.AccessorialDesc ?? "").trim()) ||
        String(item?.AccessorialCode ?? "").trim() ||
        "Accessorial Charge",
      amount: Number.isFinite(amount) ? amount : 0,
    });
  });

  return lines;
};

/**
 * Checks if the provided string is a valid email address.
 * Uses a regular expression to ensure the email has the correct format (text@text.text).
 * Returns true if the email is valid, false otherwise.
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Function to handle outside click close
 * @param ref
 * @param callback
 */
export function useOutsideClickCustom<T extends HTMLElement>(
  ref: React.RefObject<T>,
  callback: () => void
) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
}
